import type { AntennaModelRecord, EquipmentPerformanceProfile, RadioDeviceRecord, RadiationPatternPoint } from '@freedom/shared';
import { calculateLinkBudget } from './link-budget.js';

export interface EquipmentRecommendationInput {
  frequencyMHz: number;
  distanceKm: number;
  channelWidthMHz: number;
  requiredThroughputMbps: number;
  maxEirpDbm: number;
  cableLossDb: number;
  additionalLossDb: number;
  ambientNoiseFloorDbm: number;
  devices: RadioDeviceRecord[];
  antennas: AntennaModelRecord[];
  limit?: number;
  mode?: 'performance' | 'margin' | 'balanced';
  includeLegacy?: boolean;
}

export interface EquipmentRecommendation {
  deviceId: string;
  antennaId: string | null;
  manufacturerName: string;
  deviceModel: string;
  deviceType: string;
  frequencyMHz: number;
  channelWidthMHz: number;
  antennaModel: string | null;
  antennaGainDbi: number;
  maxTxPowerDbm: number;
  channelWidthsMHz: number[];
  receiverSensitivityDbm: number;
  maxThroughputMbps: number;
  maxRangeKm: number | null;
  polarization: RadioDeviceRecord['polarization'];
  performanceProfiles: EquipmentPerformanceProfile[];
  recommendedTxPowerDbm: number;
  eirpDbm: number;
  expectedRxSignalDbm: number;
  fadeMarginDb: number;
  expectedThroughputMbps: number;
  throughputEstimated: boolean;
  score: number;
  reasons: string[];
}

function normalizeAngle(angleDeg: number): number {
  return ((angleDeg % 360) + 360) % 360;
}

export function interpolateRadiationPattern(pattern: RadiationPatternPoint[], angleDeg: number, circular = true): number {
  if (pattern.length === 0) throw new Error('Radiation pattern is empty');
  if (pattern.length === 1) return pattern[0]?.attenuationDb ?? 0;
  const points = pattern.map((point) => ({ angleDeg: circular ? normalizeAngle(point.angleDeg) : point.angleDeg, attenuationDb: point.attenuationDb })).sort((left, right) => left.angleDeg - right.angleDeg);
  const requested = circular ? normalizeAngle(angleDeg) : Math.max(points[0]?.angleDeg ?? angleDeg, Math.min(points[points.length - 1]?.angleDeg ?? angleDeg, angleDeg));
  let left = points[0];
  let right = points[points.length - 1];
  for (let index = 1; index < points.length; index += 1) {
    const candidate = points[index];
    if (candidate && candidate.angleDeg >= requested) {
      left = points[index - 1];
      right = candidate;
      break;
    }
  }
  if (circular && left && right && (requested < left.angleDeg || requested > right.angleDeg)) {
    left = points[points.length - 1];
    right = points[0] ? { ...points[0], angleDeg: points[0].angleDeg + 360 } : right;
  }
  if (!left || !right || Math.abs(right.angleDeg - left.angleDeg) < 1e-9) return left?.attenuationDb ?? 0;
  const adjustedRequested = circular && requested < left.angleDeg ? requested + 360 : requested;
  const fraction = (adjustedRequested - left.angleDeg) / (right.angleDeg - left.angleDeg);
  return left.attenuationDb + (right.attenuationDb - left.attenuationDb) * fraction;
}

export function approximatePatternAttenuation(angleOffsetDeg: number, beamwidthDeg: number, frontToBackDb: number): number {
  if (beamwidthDeg <= 0 || frontToBackDb < 0) throw new RangeError('Invalid approximate antenna pattern parameters');
  const offset = Math.min(180, Math.abs(((angleOffsetDeg + 180) % 360 + 360) % 360 - 180));
  return -Math.min(frontToBackDb, 12 * (offset / beamwidthDeg) ** 2);
}

export function antennaDirectionalGainDbi(antenna: AntennaModelRecord, horizontalOffsetDeg: number, verticalOffsetDeg: number): { gainDbi: number; approximate: boolean } {
  if (antenna.peakGainDbi === null || antenna.horizontalBeamwidthDeg === null || antenna.verticalBeamwidthDeg === null || antenna.frontToBackDb === null) throw new Error('Antenna engineering specifications are incomplete');
  const horizontal = antenna.patternHorizontal.length > 0 ? interpolateRadiationPattern(antenna.patternHorizontal, horizontalOffsetDeg, true) : approximatePatternAttenuation(horizontalOffsetDeg, antenna.horizontalBeamwidthDeg, antenna.frontToBackDb);
  const vertical = antenna.patternVertical.length > 0 ? interpolateRadiationPattern(antenna.patternVertical, verticalOffsetDeg, false) : approximatePatternAttenuation(verticalOffsetDeg, antenna.verticalBeamwidthDeg, antenna.frontToBackDb);
  return { gainDbi: antenna.peakGainDbi + horizontal + vertical, approximate: antenna.patternApproximate || antenna.patternHorizontal.length === 0 || antenna.patternVertical.length === 0 };
}

export function lifecycleEligibleForRecommendation(lifecycle: RadioDeviceRecord['lifecycle'] | AntennaModelRecord['lifecycle'] | undefined, includeLegacy = false): boolean {
  return includeLegacy || (lifecycle !== 'legacy' && lifecycle !== 'discontinued');
}

export function radioAntennaCompatible(device: RadioDeviceRecord, antenna: AntennaModelRecord): boolean {
  const deviceTags = device.compatibilityTags ?? [];
  const antennaTags = antenna.compatibilityTags ?? [];
  if (deviceTags.length === 0 || antennaTags.length === 0) return true;
  return deviceTags.some((tag) => antennaTags.includes(tag));
}

export function radioEngineeringReady(device: RadioDeviceRecord): boolean {
  return device.minFrequencyMHz !== null && device.maxFrequencyMHz !== null && device.maxTxPowerDbm !== null && device.receiverSensitivityDbm !== null && device.maxThroughputMbps !== null && device.channelWidthsMHz.length > 0;
}

export function antennaEngineeringReady(antenna: AntennaModelRecord): boolean {
  return antenna.minFrequencyMHz !== null && antenna.maxFrequencyMHz !== null && antenna.peakGainDbi !== null && antenna.horizontalBeamwidthDeg !== null && antenna.verticalBeamwidthDeg !== null && antenna.frontToBackDb !== null;
}

function performanceProfile(device: RadioDeviceRecord, channelWidthMHz: number): EquipmentPerformanceProfile | undefined {
  return device.performanceProfiles.find((profile) => profile.channelWidthMHz === channelWidthMHz);
}

export function recommendEquipment(input: EquipmentRecommendationInput): EquipmentRecommendation[] {
  const deviceCandidates = input.devices.filter((device) => device.isActive
    && lifecycleEligibleForRecommendation(device.lifecycle, input.includeLegacy)
    && radioEngineeringReady(device)
    && device.minFrequencyMHz !== null
    && device.maxFrequencyMHz !== null
    && input.frequencyMHz >= device.minFrequencyMHz
    && input.frequencyMHz <= device.maxFrequencyMHz
    && device.channelWidthsMHz.includes(input.channelWidthMHz));
  const recommendations: EquipmentRecommendation[] = [];
  for (const device of deviceCandidates) {
    if (device.minFrequencyMHz === null || device.maxFrequencyMHz === null || device.maxTxPowerDbm === null || device.receiverSensitivityDbm === null || device.maxThroughputMbps === null) continue;
    const antennaCandidates: Array<AntennaModelRecord | null> = device.antennaIntegrated
      ? [null]
      : input.antennas.filter((antenna) => antenna.isActive
        && lifecycleEligibleForRecommendation(antenna.lifecycle, input.includeLegacy)
        && antennaEngineeringReady(antenna)
        && antenna.minFrequencyMHz !== null
        && antenna.maxFrequencyMHz !== null
        && input.frequencyMHz >= antenna.minFrequencyMHz
        && input.frequencyMHz <= antenna.maxFrequencyMHz
        && radioAntennaCompatible(device, antenna));
    for (const antenna of antennaCandidates) {
      if (antenna && antenna.peakGainDbi === null) continue;
      const antennaGainDbi = antenna?.peakGainDbi ?? device.antennaGainDbi ?? 0;
      const recommendedTxPowerDbm = Math.max(0, Math.min(device.maxTxPowerDbm, input.maxEirpDbm - antennaGainDbi + input.cableLossDb));
      const profile = performanceProfile(device, input.channelWidthMHz);
      const budget = calculateLinkBudget({
        frequencyGHz: input.frequencyMHz / 1_000,
        distanceKm: input.distanceKm,
        channelWidthMHz: input.channelWidthMHz,
        txPowerDbm: recommendedTxPowerDbm,
        txAntennaGainDbi: antennaGainDbi,
        rxAntennaGainDbi: antennaGainDbi,
        txCableLossDb: input.cableLossDb,
        rxCableLossDb: input.cableLossDb,
        additionalLossDb: input.additionalLossDb,
        receiverSensitivityDbm: device.receiverSensitivityDbm,
        maximumThroughputMbps: device.maxThroughputMbps,
        ambientNoiseFloorDbm: input.ambientNoiseFloorDbm,
        ...(profile ? { performanceProfile: profile } : {}),
      });
      const throughputRatio = Math.min(1.5, budget.usableThroughputMbps / Math.max(1, input.requiredThroughputMbps));
      const fadeScore = Math.max(-1, Math.min(1, budget.fadeMarginDb / 20));
      const score = input.mode === 'performance'
        ? throughputRatio * 80 + fadeScore * 15 + (budget.throughputEstimated ? 0 : 5)
        : input.mode === 'margin'
          ? throughputRatio * 25 + fadeScore * 70 + (budget.throughputEstimated ? 0 : 5)
          : throughputRatio * 55 + fadeScore * 35 + (budget.throughputEstimated ? 0 : 10);
      const reasons = [
        `${device.manufacturerName} ${device.model}: ${input.frequencyMHz / 1_000} ГГц / ${input.channelWidthMHz} МГц`,
        `Ожидаемый запас ${budget.fadeMarginDb.toFixed(1)} dB`,
        budget.throughputEstimated ? 'Пропускная способность оценочная' : `Профиль производительности: ${budget.performanceSource}`,
      ];
      if (antenna?.patternApproximate) reasons.push('Диаграмма антенны приближённая');
      recommendations.push({
        deviceId: device.id,
        antennaId: antenna?.id ?? null,
        manufacturerName: device.manufacturerName,
        deviceModel: device.model,
        deviceType: device.deviceType,
        frequencyMHz: input.frequencyMHz,
        channelWidthMHz: input.channelWidthMHz,
        antennaModel: antenna?.model ?? null,
        antennaGainDbi,
        maxTxPowerDbm: device.maxTxPowerDbm,
        channelWidthsMHz: [...device.channelWidthsMHz],
        receiverSensitivityDbm: device.receiverSensitivityDbm,
        maxThroughputMbps: device.maxThroughputMbps,
        maxRangeKm: device.maxRangeKm ?? null,
        polarization: device.polarization,
        performanceProfiles: device.performanceProfiles.map((item) => ({ ...item, entries: item.entries.map((entry) => ({ ...entry })) })),
        recommendedTxPowerDbm,
        eirpDbm: budget.eirpDbm,
        expectedRxSignalDbm: budget.rxSignalDbm,
        fadeMarginDb: budget.fadeMarginDb,
        expectedThroughputMbps: budget.usableThroughputMbps,
        throughputEstimated: budget.throughputEstimated,
        score,
        reasons,
      });
    }
  }
  return recommendations
    .filter((item) => item.expectedThroughputMbps >= input.requiredThroughputMbps && item.fadeMarginDb >= 0 && item.eirpDbm <= input.maxEirpDbm + 1e-8)
    .sort((left, right) => right.score - left.score)
    .slice(0, input.limit ?? 5);
}
